import React, { useCallback } from 'react'
import { IconRadio } from '../../../../icons'
import {
    IRadioSubscriptionPackage,
    ISingleSubscriptionPackage,
    SubscriptionPriceObject,
} from '../../helpers/BreachWallTypes'
import { PackageOptions, BreachOptions } from './SubscriptionPackage.styled'
import {
    StyledSubscriptionOriginalPrice,
    StyledSubscriptionPrice,
    StyledSubscriptionPriceQualifier,
    StyledSubscriptionRadioContainer,
    StyledSubscriptionRadioPrice,
    StyledSubscriptionRadioPriceContainer,
    StyledSubscriptionRadioQualifier,
    StyledSubscriptionRadioRibbon,
    StyledSubscriptionRadioRow,
} from './SubscriptionPriceDisplay.styled'

interface BasePriceDisplayProps {
    packageOptions: PackageOptions
    breachOptions: BreachOptions
}

export interface SingleSubscriptionPriceProps extends BasePriceDisplayProps {
    subscriptionPackage: ISingleSubscriptionPackage
}

/**
 * Display the original subscription price, the current price, and the price qualifier, this will not "radio-ify" the results at all.
 */
export const SingleSubscriptionPriceDisplay = ({
    subscriptionPackage,
    packageOptions,
    breachOptions,
}: SingleSubscriptionPriceProps) => {
    const { price, originalPrice, priceQualifier } = subscriptionPackage

    return (
        <React.Fragment>
            {originalPrice && (
                <StyledSubscriptionOriginalPrice
                    packageOptions={packageOptions}
                    breachOptions={breachOptions}
                >
                    {originalPrice}
                </StyledSubscriptionOriginalPrice>
            )}

            <StyledSubscriptionPrice
                packageOptions={packageOptions}
                breachOptions={breachOptions}
            >
                {price}
            </StyledSubscriptionPrice>

            {priceQualifier && (
                <StyledSubscriptionPriceQualifier
                    packageOptions={packageOptions}
                    breachOptions={breachOptions}
                >
                    {priceQualifier}
                </StyledSubscriptionPriceQualifier>
            )}
        </React.Fragment>
    )
}

export interface RadioSubscriptionPriceDisplayProps
    extends BasePriceDisplayProps {
    subscriptionPackage: IRadioSubscriptionPackage
    selectedPricePoint: SubscriptionPriceObject
    setSelectedPricePoint?: (newPricePoint: SubscriptionPriceObject) => void
    packagePrices: SubscriptionPriceObject[]
}

export const RadioSubscriptionPriceDisplay = ({
    subscriptionPackage,
    selectedPricePoint,
    setSelectedPricePoint,
    packagePrices,
}: RadioSubscriptionPriceDisplayProps) => {
    const hasSelectableOptions = subscriptionPackage.prices.length > 1

    const RadioRowComponent = useCallback(
        ({ pricePoint }: { pricePoint: SubscriptionPriceObject }) => {
            const {
                price,
                priceQualifier,
                ribbonOptions,
                ribbonTitle,
                packagePath,
            } = pricePoint
            const isSelected = selectedPricePoint.packagePath === packagePath

            return (
                <StyledSubscriptionRadioRow
                    onClick={() => setSelectedPricePoint?.(pricePoint)}
                    tabIndex={0}
                    role="radio"
                    aria-checked={isSelected}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setSelectedPricePoint?.(pricePoint)
                        }
                    }}
                >
                    <StyledSubscriptionRadioPriceContainer>
                        {/* ONLY give it radio if there's more than one option! */}
                        {hasSelectableOptions && (
                            <IconRadio
                                id={packagePath}
                                size={24}
                                checked={isSelected}
                            />
                        )}
                        <StyledSubscriptionRadioPrice>
                            {price}
                        </StyledSubscriptionRadioPrice>
                        {priceQualifier && (
                            <StyledSubscriptionRadioQualifier>
                                {priceQualifier}
                            </StyledSubscriptionRadioQualifier>
                        )}
                        {ribbonOptions && ribbonTitle && (
                            <StyledSubscriptionRadioRibbon
                                ribbonOptions={ribbonOptions}
                            >
                                {ribbonTitle}
                            </StyledSubscriptionRadioRibbon>
                        )}
                    </StyledSubscriptionRadioPriceContainer>
                </StyledSubscriptionRadioRow>
            )
        },
        [
            hasSelectableOptions,
            selectedPricePoint.packagePath,
            setSelectedPricePoint,
        ],
    )

    const getPricePointComponents = useCallback(
        () =>
            packagePrices.map((pricePoint, index) => (
                <RadioRowComponent key={index} pricePoint={pricePoint} />
            )),
        [packagePrices, RadioRowComponent],
    )

    return (
        <StyledSubscriptionRadioContainer>
            {getPricePointComponents()}
        </StyledSubscriptionRadioContainer>
    )
}
